import { ISelectOption } from '@private/components';
import { isAfter } from 'date-fns/isAfter';
import { parseISO } from 'date-fns/parseISO';
import { formatInTimeZone } from 'date-fns-tz';

import {
  isFilterNumberRange,
  isOption,
} from 'components/Grids/DataGrid/Filters/utils/helpers';
import {
  FILTER_OPERATIONS,
  IAppliedFilterValue,
  INumberRange,
} from 'components/Grids/DataGrid/Filters/utils/types';

import { IAllPageFilters, IGetRequestFilterOptionsParams, TSearch } from './types';

export const DELIMITER = '|||||';

const isDefinedValue = (value: unknown) => {
  if (Array.isArray(value)) {
    if (value.length === 1) {
      /*
        in case of NOT_IN/IN operator and value is empty string
        this occurs when set some value in input then remove
      */
      return value[0] !== '';
    }
    return true;
  }

  return value !== undefined && value !== '' && value !== null;
};

interface INormalizeListOfValues
  extends Pick<IAppliedFilterValue, 'disabledIndexes' | 'ignoreCase' | 'operation'> {
  columnKey: string;
  value: unknown[];
}

export const normalizeListOfValues = ({
  columnKey,
  disabledIndexes,
  ignoreCase,
  operation,
  value,
}: INormalizeListOfValues) => {
  const activeEntries = value
    .map((itemValue, index) => ({ index, itemValue }))
    .filter(({ index }) => !disabledIndexes?.includes(index));

  if (!activeEntries.length) {
    return '';
  }

  if (operation === FILTER_OPERATIONS.LIKE || operation === FILTER_OPERATIONS.NOT_LIKE) {
    return activeEntries
      .map(
        ({ itemValue }) =>
          `${columnKey}${operation}${itemValue}${ignoreCase ? `${operation}ignoreCase` : ''}`,
      )
      .join(DELIMITER);
  }

  const values = activeEntries
    .map(({ itemValue }) => {
      if (
        (typeof itemValue === 'string' && !itemValue) ||
        (isOption(itemValue) && !itemValue.value) ||
        itemValue === null
      ) {
        return;
      }

      if (typeof itemValue === 'string') {
        return itemValue;
      }

      return (itemValue as Record<string, string>)?.value;
    })
    .filter(Boolean)
    .join(';');

  if (!values) {
    return '';
  }

  return `${columnKey}${operation}${values}${ignoreCase ? `${operation}ignoreCase` : ''}`;
};

export const normalizeNumberRange = (columnKey: string, value: INumberRange) => {
  if (value?.min && value?.max) {
    return `${columnKey}>${value?.min}${DELIMITER}${columnKey}<${value?.max}`;
  }
  if (value?.min) {
    return `${columnKey}>${value?.min}`;
  }
  if (value?.max) {
    return `${columnKey}<${value?.max}`;
  }
  return '';
};

const normalizeCondition = (
  columnKey: string,
  { disabledIndexes, ignoreCase, operation, value }: IAppliedFilterValue,
) => {
  if (Array.isArray(value)) {
    return normalizeListOfValues({
      columnKey,
      disabledIndexes,
      ignoreCase,
      operation,
      value,
    });
  }

  if (isFilterNumberRange(value)) {
    return normalizeNumberRange(columnKey, value);
  }

  if (isOption(value)) {
    return `${columnKey}${operation}${value.value}${
      ignoreCase ? `${operation}ignoreCase` : ''
    }`;
  }

  return `${columnKey}${operation}${value}${ignoreCase ? `${operation}ignoreCase` : ''}`;
};

const isActiveCondition = (
  condition?: IAppliedFilterValue,
): condition is IAppliedFilterValue =>
  !!condition && isDefinedValue(condition.value) && condition.enabled !== false;

const CREATED_FILTER_KEY = 'created';

export const clampFutureEndDates = (
  filters?: IAllPageFilters,
): IAllPageFilters | undefined => {
  if (!filters?.search) {
    return filters;
  }

  const now = new Date();

  const search = Object.fromEntries(
    Object.entries(filters.search).map(([columnKey, filter]) => {
      const value = filter?.value;

      if (
        columnKey !== CREATED_FILTER_KEY ||
        !isFilterNumberRange(value) ||
        !isAfter(parseISO(value.max), now)
      ) {
        return [columnKey, filter];
      }

      return [
        columnKey,
        {
          ...filter,
          value: {
            ...value,
            max: formatInTimeZone(now, 'UTC', "yyyy-MM-dd'T'HH:mm:ssX"),
          },
        },
      ];
    }),
  );

  return { ...filters, search };
};

export const normalizeSearchFiltersPayload = (
  filters?: Partial<IAllPageFilters>,
): Record<string, number | string> => {
  const normalizedFilters: [string, number | string][] = [];

  if (!filters) {
    return {};
  }

  Object.entries<IAppliedFilterValue | number>(filters).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    // is search object
    if (typeof value === 'object') {
      const searchFilterEntries = Object.entries(value as TSearch);
      const normalizedValue = searchFilterEntries
        .flatMap(([columnKey, filterValue]) =>
          [filterValue, filterValue?.additionalCondition]
            .filter(isActiveCondition)
            .map((condition) => normalizeCondition(columnKey, condition)),
        )
        .filter(Boolean)
        .join(DELIMITER);

      if (normalizedValue) {
        return normalizedFilters.push([key, normalizedValue]);
      }
      return;
    }

    normalizedFilters.push([key, value]);
  });

  return Object.fromEntries(normalizedFilters);
};

export const getRequestFilterOptions = ({
  columnFilterValues,
  columnKey,
  normalizer,
  requestFilterData,
}: IGetRequestFilterOptionsParams) => {
  const defaultOption = (option: string) => ({
    label: option.toString(),
    value: option,
  });

  const options =
    requestFilterData?.fieldValues.map((item) => {
      return (
        normalizer[columnKey as keyof typeof normalizer]?.({
          isFilter: true,
          value: item,
        }) || defaultOption(item)
      );
    }) || [];

  if (columnFilterValues) {
    const additionalValues = (
      Array.isArray(columnFilterValues) ? columnFilterValues : [columnFilterValues]
    ).filter((filterItem) => {
      return (
        typeof filterItem !== 'string' &&
        !(options as ISelectOption[]).some(
          (optionItem) =>
            optionItem?.label === filterItem?.label &&
            optionItem?.value === filterItem?.value,
        )
      );
    });

    return [...additionalValues, ...options];
  }

  return options;
};
